import { isAttachmentOfficeMimeType } from "@rakazo/contracts";
import { PREVIEW_ARTIFACT_MIME_TYPE } from "@rakazo/core";

/**
 * Artifact panel (PIV-36, DSK-2): which view an artifact gets and when it is too
 * big to render. Pure functions; the panel and the gallery share them.
 */

/** How the panel shows an artifact; pdf and document (docx, xlsx, pptx) open in the markup view (RAK-168). */
export type ArtifactViewKind =
  | "markdown"
  | "code"
  | "html"
  | "pdf"
  /** Word, Excel, PowerPoint: shown as the server's PDF rendering (RAK-168), never inline. */
  | "document"
  | "image"
  | "table"
  | "preview"
  | "unknown";

/** Text contents above this are offered for download only, never rendered. */
export const ARTIFACT_TEXT_RENDER_LIMIT_BYTES = 5 * 1024 * 1024;
/** Images above this are offered for download only. */
export const ARTIFACT_IMAGE_RENDER_LIMIT_BYTES = 20 * 1024 * 1024;

/** Raster formats shown in an <img>. SVG is an image too, but only ever via <img>, never inline. */
const IMAGE_MIME_TYPES = new Set([
  "image/png",
  "image/jpeg",
  "image/gif",
  "image/webp",
  "image/avif",
  "image/svg+xml",
]);

/** Office files the server converts to PDF for the markup view (RAK-168). */
const OFFICE_EXTENSIONS = new Set(["docx", "xlsx", "pptx"]);

/** Whether the panel shows this kind in the markup view (PDF rendering via pdf.js on canvas). */
export function isMarkupArtifactView(kind: ArtifactViewKind): boolean {
  return kind === "pdf" || kind === "document";
}

/** File extension → Prism language for code artifacts. */
const CODE_LANGUAGES: Record<string, string> = {
  ts: "typescript",
  tsx: "tsx",
  js: "javascript",
  mjs: "javascript",
  cjs: "javascript",
  jsx: "jsx",
  json: "json",
  py: "python",
  sh: "bash",
  bash: "bash",
  css: "css",
  scss: "scss",
  sql: "sql",
  yaml: "yaml",
  yml: "yaml",
  go: "go",
  rs: "rust",
  java: "java",
  kt: "kotlin",
  swift: "swift",
  c: "c",
  h: "c",
  cpp: "cpp",
  rb: "ruby",
  php: "php",
  xml: "markup",
  toml: "toml",
  graphql: "graphql",
  diff: "diff",
};

const CODE_MIME_TYPES: Record<string, string> = {
  "application/json": "json",
  "application/javascript": "javascript",
  "text/javascript": "javascript",
  "application/typescript": "typescript",
  "text/x-python": "python",
  "text/css": "css",
  "application/sql": "sql",
  "application/x-sh": "bash",
  "application/yaml": "yaml",
  "text/yaml": "yaml",
  "application/xml": "markup",
  "text/xml": "markup",
};

function extensionOf(name: string): string {
  const dot = name.lastIndexOf(".");
  return dot > 0 ? name.slice(dot + 1).toLowerCase() : "";
}

function baseMime(mimeType: string): string {
  return mimeType.split(";")[0]?.trim().toLowerCase() ?? "";
}

/** Prism language for a code artifact, or null when it is not code. */
export function artifactCodeLanguage(name: string, mimeType: string): string | null {
  return CODE_MIME_TYPES[baseMime(mimeType)] ?? CODE_LANGUAGES[extensionOf(name)] ?? null;
}

/** Delimiter of a table artifact, or null when it is not CSV/TSV. */
export function artifactTableDelimiter(name: string, mimeType: string): "," | "\t" | null {
  const mime = baseMime(mimeType);
  const extension = extensionOf(name);
  if (mime === "text/tab-separated-values" || extension === "tsv") return "\t";
  if (mime === "text/csv" || extension === "csv") return ",";
  return null;
}

/**
 * The view an artifact gets; media type first, file name as fallback. Office files without
 * macros become document; old binary and macro formats stay unknown (download only).
 */
export function artifactViewKind(name: string, mimeType: string): ArtifactViewKind {
  const mime = baseMime(mimeType);
  const extension = extensionOf(name);
  if (mime === PREVIEW_ARTIFACT_MIME_TYPE) return "preview";
  if (mime === "text/markdown" || extension === "md" || extension === "markdown") return "markdown";
  if (mime === "text/html" || extension === "html" || extension === "htm") return "html";
  if (mime === "application/pdf" || extension === "pdf") return "pdf";
  if (isAttachmentOfficeMimeType(mime) || OFFICE_EXTENSIONS.has(extension)) return "document";
  if (IMAGE_MIME_TYPES.has(mime)) return "image";
  if (artifactTableDelimiter(name, mimeType)) return "table";
  if (artifactCodeLanguage(name, mimeType)) return "code";
  if (mime === "text/plain" || extension === "txt" || extension === "log") return "code";
  return "unknown";
}

/** Views whose source is text: they get the Source tab and the copy button. */
export function isTextArtifactView(kind: ArtifactViewKind): boolean {
  return kind === "markdown" || kind === "code" || kind === "html" || kind === "table";
}

/** Whether the panel may render this artifact, or only offer the download. */
export function canRenderArtifact(kind: ArtifactViewKind, size: number): boolean {
  if (kind === "unknown") return false;
  if (kind === "image") return size <= ARTIFACT_IMAGE_RENDER_LIMIT_BYTES;
  if (isTextArtifactView(kind)) return size <= ARTIFACT_TEXT_RENDER_LIMIT_BYTES;
  return true;
}

/**
 * Splits CSV/TSV text into rows (RFC 4180 quoting: "" inside quotes, line breaks
 * inside quoted fields). Never evaluates content; cells stay plain strings.
 */
export function parseDelimited(text: string, delimiter: "," | "\t"): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = "";
  let quoted = false;
  const source = text.startsWith("\uFEFF") ? text.slice(1) : text;
  for (let index = 0; index < source.length; index += 1) {
    const char = source[index];
    if (quoted) {
      if (char === '"') {
        if (source[index + 1] === '"') {
          cell += '"';
          index += 1;
        } else {
          quoted = false;
        }
      } else {
        cell += char;
      }
      continue;
    }
    if (char === '"' && cell.length === 0) {
      quoted = true;
    } else if (char === delimiter) {
      row.push(cell);
      cell = "";
    } else if (char === "\n" || char === "\r") {
      if (char === "\r" && source[index + 1] === "\n") index += 1;
      row.push(cell);
      rows.push(row);
      row = [];
      cell = "";
    } else {
      cell += char;
    }
  }
  if (cell.length > 0 || row.length > 0) {
    row.push(cell);
    rows.push(row);
  }
  return rows;
}
